import { track } from '@omnea-app/fe-analytics/useAnalytics'
import type { DashboardGrantRole } from './hooks/useDashboardAccess'
import type { Dashboard } from './hooks/useDashboards'
import type { DashboardOrgAccess } from './hooks/useUpdateDashboardOrgAccess'

type ShareDialogSource = 'dashboard' | 'catalog'

// The undo triggers are the user reversing a copy, not pruning a widget — analysis nets them off
// DashboardWidgetDuplicated / DashboardWidgetSavedAsNew rather than counting them as intentional
// deletions.
type WidgetDeleteTrigger = 'widget-menu' | 'duplicate-undo' | 'save-as-new-undo'

declare module '@omnea-app/packages/universal/analytics' {
  interface AnalyticsEvents {
    CustomDashboardHomepageViewed: Record<string, never>
    CustomDashboardOpened: {
      dashboardId: string
      widgetCount: number
      visibility: Dashboard['visibility']
      userRole: Dashboard['userRole']
    }
    CreateCustomDashboardClicked: Record<string, never>
    CustomDashboardDuplicated: {
      dashboardId: string
      newDashboardId: string
      visibility: Dashboard['visibility']
      userRole: Dashboard['userRole']
    }
    CustomDashboardDeleted: { dashboardId: string; visibility: string }
    DashboardWidgetOpened: { dashboardId: string; widgetId: string; source: string; chartType: string }
    DashboardWidgetReordered: {
      dashboardId: string
      widgetId: string
      fromIndex: number
      toIndex: number
      userRole: Dashboard['userRole']
    }
    DashboardWidgetDeleted: {
      dashboardId: string
      widgetId: string
      source: string
      chartType: string
      userRole: Dashboard['userRole']
      trigger: WidgetDeleteTrigger
    }
    DashboardWidgetDuplicated: {
      dashboardId: string
      widgetId: string
      source: string
      chartType: string
      destinationType: 'current' | 'existing' | 'new'
      userRole: Dashboard['userRole']
    }
    DashboardWidgetSavedAsNew: {
      dashboardId: string
      widgetId: string
      source: string
      chartType: string
      destinationType: 'current' | 'existing' | 'new'
      userRole: Dashboard['userRole']
    }
    AddWidgetClicked: { dashboardId: string }
    DashboardTimeFilterChanged: { dashboardId: string; timeRange: string }
    WidgetSourceSelected: { source: string }
    WidgetMetricSelected: { source: string; metricGroup: string; metricKey: string; selected: boolean }
    WidgetBreakdownChanged: { source: string; breakdownKey: string; index: number }
    WidgetBreakdownAdded: { source: string; breakdownCount: number }
    WidgetFilterChanged: { source: string; filterKey: string; index: number }
    WidgetFilterAdded: { source: string; filterCount: number }
    WidgetDrillDownClicked: { source: string; metricKey: string }
    WidgetChartTypeChanged: { source: string; chartType: string }
    WidgetTooltipViewed: { source: string; context: string }
    CustomDashboardOnboardingShown: Record<string, never>
    CustomDashboardOnboardingNextClicked: { step: number; isLastStep: boolean }
    CustomDashboardWhatsNewClicked: Record<string, never>
    CustomDashboardHomeBannerClicked: Record<string, never>
    ShareDashboardDialogOpened: {
      dashboardId: string
      source: ShareDialogSource
      visibility: Dashboard['visibility']
      userRole: Dashboard['userRole']
      sharedWithCount: number
    }
    DashboardAccessGranted: { dashboardId: string; role: DashboardGrantRole; granteeCount: number }
    DashboardAccessRoleChanged: { dashboardId: string; fromRole: DashboardGrantRole; toRole: DashboardGrantRole }
    DashboardAccessRemoved: { dashboardId: string; role: DashboardGrantRole }
    DashboardOrgAccessChanged: { dashboardId: string; fromOrgAccess: DashboardOrgAccess; toOrgAccess: DashboardOrgAccess }
    DashboardShareLinkCopied: { dashboardId: string }
  }
}

export const customDashboardAnalytics = {
  homepageViewed: () => track('CustomDashboardHomepageViewed', {}),
  dashboardOpened: (props: {
    dashboardId: string
    widgetCount: number
    visibility: Dashboard['visibility']
    userRole: Dashboard['userRole']
  }) => track('CustomDashboardOpened', props),
  createDashboardClicked: () => track('CreateCustomDashboardClicked', {}),
  dashboardDuplicated: (props: {
    dashboardId: string
    newDashboardId: string
    visibility: Dashboard['visibility']
    userRole: Dashboard['userRole']
  }) => track('CustomDashboardDuplicated', props),
  dashboardDeleted: (props: { dashboardId: string; visibility: string }) => track('CustomDashboardDeleted', props),
  widgetOpened: (props: { dashboardId: string; widgetId: string; source: string; chartType: string }) =>
    track('DashboardWidgetOpened', props),
  widgetReordered: (props: {
    dashboardId: string
    widgetId: string
    fromIndex: number
    toIndex: number
    userRole: Dashboard['userRole']
  }) => track('DashboardWidgetReordered', props),
  widgetDeleted: (props: {
    dashboardId: string
    widgetId: string
    source: string
    chartType: string
    userRole: Dashboard['userRole']
    trigger: WidgetDeleteTrigger
  }) => track('DashboardWidgetDeleted', props),
  widgetDuplicated: (props: {
    dashboardId: string
    widgetId: string
    source: string
    chartType: string
    destinationType: 'current' | 'existing' | 'new'
    userRole: Dashboard['userRole']
  }) => track('DashboardWidgetDuplicated', props),
  widgetSavedAsNew: (props: {
    dashboardId: string
    widgetId: string
    source: string
    chartType: string
    destinationType: 'current' | 'existing' | 'new'
    userRole: Dashboard['userRole']
  }) => track('DashboardWidgetSavedAsNew', props),
  addWidgetClicked: (props: { dashboardId: string }) => track('AddWidgetClicked', props),
  timeFilterChanged: (props: { dashboardId: string; timeRange: string }) => track('DashboardTimeFilterChanged', props),
  sourceSelected: (props: { source: string }) => track('WidgetSourceSelected', props),
  metricSelected: (props: { source: string; metricGroup: string; metricKey: string; selected: boolean }) =>
    track('WidgetMetricSelected', props),
  breakdownChanged: (props: { source: string; breakdownKey: string; index: number }) => track('WidgetBreakdownChanged', props),
  breakdownAdded: (props: { source: string; breakdownCount: number }) => track('WidgetBreakdownAdded', props),
  filterChanged: (props: { source: string; filterKey: string; index: number }) => track('WidgetFilterChanged', props),
  filterAdded: (props: { source: string; filterCount: number }) => track('WidgetFilterAdded', props),
  drillDownClicked: (props: { source: string; metricKey: string }) => track('WidgetDrillDownClicked', props),
  chartTypeChanged: (props: { source: string; chartType: string }) => track('WidgetChartTypeChanged', props),
  tooltipViewed: (props: { source: string; context: string }) => track('WidgetTooltipViewed', props),
  onboardingShown: () => track('CustomDashboardOnboardingShown', {}),
  onboardingNextClicked: (props: { step: number; isLastStep: boolean }) => track('CustomDashboardOnboardingNextClicked', props),
  whatsNewClicked: () => track('CustomDashboardWhatsNewClicked', {}),
  homeBannerClicked: () => track('CustomDashboardHomeBannerClicked', {}),
  shareDialogOpened: (props: {
    dashboardId: string
    source: ShareDialogSource
    visibility: Dashboard['visibility']
    userRole: Dashboard['userRole']
    sharedWithCount: number
  }) => track('ShareDashboardDialogOpened', props),
  accessGranted: (props: { dashboardId: string; role: DashboardGrantRole; granteeCount: number }) =>
    track('DashboardAccessGranted', props),
  accessRoleChanged: (props: { dashboardId: string; fromRole: DashboardGrantRole; toRole: DashboardGrantRole }) =>
    track('DashboardAccessRoleChanged', props),
  accessRemoved: (props: { dashboardId: string; role: DashboardGrantRole }) => track('DashboardAccessRemoved', props),
  orgAccessChanged: (props: { dashboardId: string; fromOrgAccess: DashboardOrgAccess; toOrgAccess: DashboardOrgAccess }) =>
    track('DashboardOrgAccessChanged', props),
  shareLinkCopied: (props: { dashboardId: string }) => track('DashboardShareLinkCopied', props),
}
